import * as React from 'react'

import { Box, Form, Text } from '@factorialco/deprecated-design-system'
import {
  PerformanceProcessTemplateFeaturesFragment,
  UpdatePerformanceProcessTemplateFeaturesDocument,
  UpdatePerformanceProcessTemplateFeaturesMutationVariables,
} from 'generated/resources/graphql'

import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import {
  SettingsFeatures,
  type TypeSettingsFeatures,
} from 'modules/performance_process/components/SettingsFeatures'

type Form = TypeSettingsFeatures & {
  id: number
}

export const CustomTemplateSettingsFeatures: React.FC<{
  template: PerformanceProcessTemplateFeaturesFragment
}> = ({ template }) => {
  const [updateFeatures, { loading }] = useMutation(
    UpdatePerformanceProcessTemplateFeaturesDocument,
    {
      extractorFn: (data) =>
        data.performanceMutations.updateFeaturesPerformanceReviewProcessCustomTemplate,
    }
  )

  const form = useForm<Form>({
    defaultValues: {
      id: Number(template.id),
      agreementsConfiguration: template.agreementsEnabled ?? false,
      agreementsEsignatureConfiguration: template.agreementsEsignatureEnabled ?? false,
      employeePotentialScoreConfiguration: template.employeePotentialScoreEnabled ?? false,
      competenciesAssessmentsConfiguration: template.competenciesAssessmentsEnabled ?? false,
      republishEvaluationsConfiguration: template.republishEvaluationsEnabled ?? false,
    },
    validate: (data) => {
      const errors: FormErrors<UpdatePerformanceProcessTemplateFeaturesMutationVariables> = {}
      if (!data.id) {
        errors.id = i18n.t('common.empty')
      }

      return errors
    },
    onSubmit: (data) => {
      return updateFeatures({
        variables: {
          id: template.id,
          agreementsEnabled: data.agreementsConfiguration,
          agreementsEsignatureEnabled: data.agreementsEsignatureConfiguration,
          employeePotentialScoreEnabled: data.employeePotentialScoreConfiguration,
          competenciesAssessmentsEnabled: data.competenciesAssessmentsConfiguration,
          republishEvaluationsEnabled: data.republishEvaluationsConfiguration,
        },
      }).then((response) => handleResult(response, () => {}))
    },
  })

  const onChange = (field: keyof TypeSettingsFeatures, enabled: boolean) => {
    if (field === 'agreementsConfiguration') {
      form.setValues({
        ...form.values,
        agreementsConfiguration: enabled,
        agreementsEsignatureConfiguration: enabled
          ? form.values.agreementsEsignatureConfiguration
          : false,
      })
    } else {
      form.setValues({ ...form.values, [field]: enabled })
    }

    form.handleSubmit()
  }

  const disabled = form.isSubmitting || loading || !form.isValid

  return (
    <Box gap="s32">
      <Box gap="s16">
        <Text size="400" color="grey1000" weight="semibold">
          {i18n.t('performance_process.reviews.features.title')}
        </Text>
        <Text size="300">{i18n.t('performance_process.reviews.features.description')}</Text>
      </Box>
      <Form onSubmit={() => form.handleSubmit()} padding={null}>
        <SettingsFeatures
          disabled={disabled}
          features={{
            agreementsConfiguration: form.values.agreementsConfiguration,
            agreementsEsignatureConfiguration: form.values.agreementsEsignatureConfiguration,
            employeePotentialScoreConfiguration: form.values.employeePotentialScoreConfiguration,
            competenciesAssessmentsConfiguration: form.values.competenciesAssessmentsConfiguration,
            republishEvaluationsConfiguration: form.values.republishEvaluationsConfiguration,
          }}
          onFeatureToggled={onChange}
        />
      </Form>
    </Box>
  )
}
